import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import whatsappService from "../services/whatsappService";

const UpdateWhatsApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("No token provided");
        setVerifying(false);
        return;
      }

      try {
        await whatsappService.verifyToken(token);
        setVerifying(false);
      } catch (error: any) {
        setError(error.message);
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await whatsappService.updateNumber(
        token,
        whatsappNumber
      );
      setSuccess(response.message);
      // Redirect to profile page after 3 seconds
      setTimeout(() => navigate("/profile"), 3000);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verifying update link...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !token) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/profile")}
            sx={{ mt: 2 }}
          >
            Return to Profile
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Update WhatsApp Number
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="WhatsApp Number"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              placeholder="Enter your WhatsApp number with country code (e.g., +1234567890)"
              margin="normal"
              required
              disabled={loading || !!success}
              error={!!error}
              helperText={
                error
                  ? error
                  : "Enter your WhatsApp number with country code (e.g., +1234567890)"
              }
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !!success}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Update Number"}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default UpdateWhatsApp;
